import type { Vec2 } from '@interspacial/shared';

/**
 * Convex hull of a point set, by Andrew's monotone chain.
 *
 * Used to outline a cluster on the galaxy map. Computed on the XY footprint
 * rather than in 3D: a true 3D hull would be a solid, and the point here is a
 * single readable line around the group, not a shell that hides what is inside.
 *
 * Returns points in counter-clockwise order. Fewer than three distinct points
 * has no hull, so the input comes back as-is.
 */
export function convexHull(points: readonly Vec2[]): Vec2[] {
  if (points.length < 3) return [...points];

  const sorted = [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));

  // > 0 when the turn from o->a to o->b is counter-clockwise.
  const cross = (o: Vec2, a: Vec2, b: Vec2): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const build = (input: readonly Vec2[]): Vec2[] => {
    const chain: Vec2[] = [];
    for (const point of input) {
      while (
        chain.length >= 2 &&
        cross(chain[chain.length - 2]!, chain[chain.length - 1]!, point) <= 0
      ) {
        chain.pop();
      }
      chain.push(point);
    }
    chain.pop(); // shared with the other chain's start
    return chain;
  };

  const hull = [...build(sorted), ...build([...sorted].reverse())];
  return hull.length >= 3 ? hull : [...points];
}

/** Push hull points outward from their centroid, so the outline clears the stars. */
export function expandHull(hull: readonly Vec2[], margin: number): Vec2[] {
  if (hull.length === 0) return [];

  const cx = hull.reduce((sum, p) => sum + p.x, 0) / hull.length;
  const cy = hull.reduce((sum, p) => sum + p.y, 0) / hull.length;

  return hull.map((p) => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    const length = Math.hypot(dx, dy);
    if (length < 1e-9) return { x: p.x, y: p.y };
    return { x: p.x + (dx / length) * margin, y: p.y + (dy / length) * margin };
  });
}
